import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import LoginSignupModal, { LoginSignupModalView } from "@/components/ui/login-signup";
import { translateAuthError } from "@/lib/auth-errors";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: LoginSignupModalView;
  redirectTo?: string;
}

export function AuthModal({ open, onOpenChange, defaultTab = "login", redirectTo }: AuthModalProps) {
  const [tab, setTab] = useState<LoginSignupModalView>(defaultTab);
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (open) {
      setTab(defaultTab);
      setForgotMode(false);
    }
  }, [defaultTab, open]);

  const handleAuthSubmit = async (
    data: { email: string; password: string; name?: string },
    view: LoginSignupModalView,
  ) => {
    if (!data.email || !data.password) return;

    setLoading(true);
    setPendingEmail(data.email);

    if (view === "login") {
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      setLoading(false);
      if (error) {
        toast({ title: "Erro ao entrar", description: translateAuthError(error.message), variant: "destructive" });
        return;
      }
    } else {
      const { data: resData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: { full_name: data.name },
        },
      });

      setLoading(false);
      if (error) {
        toast({ title: "Erro ao criar conta", description: translateAuthError(error.message), variant: "destructive" });
        return;
      }

      if (resData?.user && !resData?.session) {
        setPendingEmail(data.email);
        setTab("verify-otp");
        return;
      } else {
        toast({ title: "Conta criada com sucesso!" });
      }
    }

    onOpenChange(false);

    type RedirectState = { from?: { pathname?: string } };
    const from = redirectTo || (location.state as unknown as RedirectState)?.from?.pathname;
    if (from && from !== "/" && from !== location.pathname) {
      navigate(from, { replace: true });
    }
  };

  const handleVerifyOtp = async (emailToVerify: string, code: string) => {
    const targetEmail = emailToVerify || pendingEmail;
    if (!targetEmail || !code) return;
    setLoading(true);

    // Try signup OTP first
    let { data, error } = await supabase.auth.verifyOtp({
      email: targetEmail,
      token: code,
      type: "signup",
    });

    if (error) {
      // Try recovery OTP if signup OTP failed
      const recoveryRes = await supabase.auth.verifyOtp({
        email: targetEmail,
        token: code,
        type: "recovery",
      });
      data = recoveryRes.data;
      error = recoveryRes.error;
    }

    setLoading(false);

    if (error) {
      toast({
        title: "Código inválido ou expirado",
        description: translateAuthError(error.message),
        variant: "destructive",
      });
      return;
    }

    toast({ title: "E-mail verificado com sucesso! 🎉" });
    onOpenChange(false);

    type RedirectState = { from?: { pathname?: string } };
    const from = redirectTo || (location.state as unknown as RedirectState)?.from?.pathname;
    if (from && from !== "/" && from !== location.pathname) {
      navigate(from, { replace: true });
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${window.location.origin}/recuperar-senha`,
    });
    setLoading(false);
    if (error) {
      toast({ title: "Erro", description: translateAuthError(error.message), variant: "destructive" });
    } else {
      setPendingEmail(forgotEmail);
      setForgotMode(false);
      setTab("verify-otp");
    }
  };

  if (forgotMode) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="w-full h-full max-sm:min-h-[100dvh] max-sm:rounded-none max-sm:border-none sm:h-auto sm:max-h-[92dvh] sm:max-w-[400px] sm:rounded-2xl bg-card text-foreground border border-border/80 shadow-2xl p-6 max-sm:p-5 max-sm:py-6 overflow-y-auto"
          aria-describedby="auth-forgot-desc"
          style={{ touchAction: "manipulation" }}
        >
          <DialogTitle className="sr-only">Redefinir senha</DialogTitle>
          <DialogDescription id="auth-forgot-desc" className="sr-only">
            Enviaremos um link para redefinir sua senha.
          </DialogDescription>
          <div className="flex flex-col justify-between h-full min-h-full space-y-6" style={{ touchAction: "manipulation" }}>
            <div className="flex items-center justify-between w-full flex-shrink-0">
              <button
                type="button"
                onClick={() => setForgotMode(false)}
                className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors select-none"
                style={{ touchAction: "manipulation" }}
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <span className="text-xs text-muted-foreground font-medium select-none">
                Português (Brasil)
              </span>
            </div>

            <div className="flex-1 flex flex-col justify-center my-auto py-2 space-y-5">
              <div className="flex flex-col items-center justify-center my-2 text-center">
                <h2 className="font-display text-xl font-bold text-foreground mb-1">Esqueceu a senha?</h2>
                <p className="text-xs text-muted-foreground max-w-[280px]">
                  Insira o seu e-mail e enviaremos um código de 6 dígitos para você redefinir a sua senha.
                </p>
              </div>

              <form onSubmit={handleForgotPassword} className="space-y-3.5 w-full" style={{ touchAction: "manipulation" }}>
                <div className="relative flex items-center h-12 w-full rounded-xl border border-border/80 bg-secondary/30 px-4 transition-all focus-within:border-primary focus-within:bg-background">
                  <input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="E-mail (ex: seu@email.com)"
                    required
                    className="w-full bg-transparent p-0 text-[16px] text-foreground placeholder:text-muted-foreground/60 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 border-none shadow-none"
                    style={{ fontSize: "16px", touchAction: "manipulation" }}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl text-base font-semibold transition-all active:scale-[0.98] shadow-none bg-primary text-primary-foreground hover:opacity-90"
                  disabled={loading}
                  style={{ touchAction: "manipulation" }}
                >
                  {loading ? "Enviando..." : "Enviar código de verificação"}
                </Button>
              </form>
            </div>

            <div className="flex flex-col items-center gap-4 w-full flex-shrink-0 pt-4">
              <button
                type="button"
                className="w-full h-12 rounded-xl border border-primary text-primary font-semibold text-base hover:bg-primary/10 transition-colors select-none flex items-center justify-center"
                style={{ touchAction: "manipulation" }}
                onClick={() => setForgotMode(false)}
              >
                Voltar para o login
              </button>

              <div className="flex items-center justify-center opacity-70 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                TicketHall
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <LoginSignupModal
      open={open}
      onOpenChange={onOpenChange}
      defaultView={tab}
      loading={loading}
      pendingEmail={pendingEmail}
      onViewChange={(view) => setTab(view)}
      onSubmit={handleAuthSubmit}
      onVerifyOtp={handleVerifyOtp}
      onSocialLogin={undefined}
      onForgotPassword={() => setForgotMode(true)}
    />
  );
}
